import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { tokens } from '../theme';
import { roleLabel } from '../utils/roleLabels';

const drawerWidth = 232;
const mobileBarHeight = 48;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isKalotteUser, canAccessProduction, isOperator } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = [
    { text: 'Lieferanten Zertifikate', path: '/certificates', show: !isOperator },
    { text: 'Chargen', path: '/charges', show: !isOperator },
    { text: 'Produktion', path: '/production', show: canAccessProduction },
    { text: 'Stammdaten', path: '/master-data', show: isKalotteUser },
    { text: 'Benutzer', path: '/users', show: isKalotteUser },
  ];

  const go = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: tokens.sidebar }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Kalotte</Typography>
        <Typography variant="caption" color="text.secondary">
          Werkszeugnisse
        </Typography>
      </Box>
      <List sx={{ px: 1, flexGrow: 1 }} dense>
        {menuItems
          .filter((item) => item.show)
          .map((item) => {
            const selected = location.pathname.startsWith(item.path);
            return (
              <ListItemButton
                key={item.text}
                onClick={() => go(item.path)}
                selected={selected}
                sx={{
                  mb: 0.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: selected ? tokens.border : 'transparent',
                  bgcolor: selected ? '#fff' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#fff' },
                  },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: selected ? 500 : 400 }}
                />
              </ListItemButton>
            );
          })}
      </List>
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${tokens.border}` }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
          {user?.full_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {roleLabel(user?.role)}
        </Typography>
        {isKalotteUser && (
          <ListItemButton onClick={() => go('/dymo-test')} sx={{ px: 0, mt: 0.5, borderRadius: 1 }}>
            <ListItemText primary="Drucker" primaryTypographyProps={{ fontSize: 12 }} />
          </ListItemButton>
        )}
        <ListItemButton onClick={handleLogout} sx={{ px: 0, borderRadius: 1 }}>
          <ListItemText primary="Abmelden" primaryTypographyProps={{ fontSize: 12 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fff' }}>
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          zIndex: (t) => t.zIndex.drawer + 1,
          top: 0,
          left: 0,
          right: 0,
          height: mobileBarHeight,
          alignItems: 'center',
          px: 1,
          bgcolor: '#fff',
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <IconButton aria-label="Menü öffnen" onClick={() => setMobileOpen(true)}>
          <MenuIcon />
        </IconButton>
        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: 14 }}>Kalotte</Typography>
      </Box>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="Hauptnavigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              border: 'none',
              bgcolor: tokens.sidebar,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              borderRight: `1px solid ${tokens.border}`,
              bgcolor: tokens.sidebar,
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 3.5 },
          py: { xs: 2, md: 3 },
          pt: { xs: `${mobileBarHeight + 16}px`, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
